import type { JSX } from 'react';

import clsx from 'clsx';

import { testIds } from '@/components/constants/testIds';
import { composeAlignmentClasses } from '@/components/functions/composeAlignmentClasses';
import { composeContentAlignmentClasses } from '@/components/functions/composeContentAlignmentClasses';
import { composeGapSizeClasses } from '@/components/functions/composeGapSizeClasses';
import { composeGridColumnsClasses } from '@/components/functions/composeGridColumnsClasses';
import { composeHorizontalGapSizeClasses } from '@/components/functions/composeHorizontalGapSizeClasses';
import { composeJustifyClasses } from '@/components/functions/composeJustifyClasses';
import { composeJustifyItemsClasses } from '@/components/functions/composeJustifyItemsClasses';
import { composeVerticalGapSizeClasses } from '@/components/functions/composeVerticalGapSizeClasses';
import type { Adaptive } from '@/components/types/Adaptive';
import type { Alignment } from '@/components/types/Alignment';
import type { ContentAlignment } from '@/components/types/ContentAlignment';
import type { Justify } from '@/components/types/Justify';
import type { JustifyItems } from '@/components/types/JustifyItems';

type GridProps = {
    children: React.ReactNode;
    columns?: Adaptive<number>;
    justify?: Adaptive<Justify>;
    justifyItems?: Adaptive<JustifyItems>;
    alignment?: Adaptive<Alignment>;
    contentAlignment?: Adaptive<ContentAlignment>;
    gap?: Adaptive<number>;
    hGap?: Adaptive<number>;
    vGap?: Adaptive<number>;
    className?: string;
    testId?: string;
};

export default function Grid({
    children,
    columns,
    justify,
    justifyItems,
    alignment,
    contentAlignment,
    gap,
    hGap,
    vGap,
    className,
    testId = testIds.grid.base,
}: GridProps): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <div
            data-testid={testId}
            className={clsx(
                'grid',
                columns && composeGridColumnsClasses(columns),
                justify && composeJustifyClasses(justify),
                justifyItems && composeJustifyItemsClasses(justifyItems),
                alignment && composeAlignmentClasses(alignment),
                contentAlignment && composeContentAlignmentClasses(contentAlignment),
                gap && composeGapSizeClasses(gap),
                hGap && composeHorizontalGapSizeClasses(hGap),
                vGap && composeVerticalGapSizeClasses(vGap),
                className
            )}
        >
            {children}
        </div>
    );
}
